import type { AccessGroup } from '@/api/auth';

/**
 * The group question §6.8's controls ask, against the rows of
 * `backend/tests/test_permission_matrix.py` that answer it.
 *
 * ⚠️ **This decides what is *rendered*, never what may be *read* or *written*** — the standing
 * obligation of 2026-07-25, which `RequireGroup` and `candidateAccess.ts` both state. The API refuses a
 * Recruiter on its own; a client-side check that vanished would cost a 403 on a request, not access to
 * anything. What it buys is §5's property — *"not by disabling items, but by not rendering them"* — and
 * an empty state whose one action is not a button that answers 403.
 */

/**
 * Whether this viewer may **write** a vacancy: create one, edit one, or close one.
 *
 * **Two matrix rows, and they are the same row twice** over the columns *anon · Interviewer · Recruiter
 * · Admin · Superuser · Superuser+Admin*:
 *
 *  * `Route("post", ROLES, ROLE_BODY, (401, 403, 403, 201, 403, 403))`
 *  * `Route("patch", ROLE, {"title": "Edited"}, (401, 403, 403, 200, 403, 403))`
 *
 * Both routes declare the same dependency — `write_access = require_group(GROUP_ADMIN)` in
 * `backend/src/apps/vacancies/api.py` — so the **Recruiter column is 403** on both, which is the whole
 * asymmetry of this screen: a Recruiter reads the catalogue (`get ROLES` → `200`) and changes nothing
 * in it.
 *
 * ⚠️ **It is deliberately *not* `mayReadVacancyCatalogue`** (`candidateAccess.ts`), whose row admits a
 * Recruiter. One predicate covers both writes because the two rows are identical in every column and
 * come from one dependency; that is the opposite reasoning from `mayWriteAboutPerson` versus
 * `mayAnonymizePerson`, where the bodies looked identical and the rows were not. If a vacancy route is
 * ever narrowed differently from the other, it gets a predicate of its own rather than widening this
 * one.
 *
 * **TASK-071 calls it for the *New vacancy* action of the empty state; TASK-072's row menu — Edit,
 * Close and Reopen — is the second caller and the reason the name is about writing rather than about
 * creating.**
 */
export const mayWriteVacancies = (groups: AccessGroup[]): boolean => groups.includes('Admin');
